import { useState } from "react";
import { useParams, useLocation } from "wouter";
import { useAssets } from "@/contexts/AssetContext.jsx";
import { Badge } from "@/components/ui/badge.jsx";
import { Button } from "@/components/ui/button.jsx";
import { ArrowLeft, Cpu } from "lucide-react";
import Sidebar from "@/components/sidebar.jsx";
import MobileMenuButton from "@/components/ui/mobile-menu-button.jsx";
import AssetNavigationTabs from "@/components/asset-navigation-tabs.jsx";
import CopyCoordinatesButton from "@/components/copy-coordinates-button.jsx";
import {
  getStatusBadgeClass,
  getStatusDotClass,
  getStatusText,
} from "@/lib/status-utils.js";
import { LoadingSkeleton } from "@/components/ui/loading.jsx";
import { buildBackToSourcePath } from "@/lib/table-url-state.js";
import PageHeader from "@/components/page-header.jsx";
import PageHeaderActions from "@/components/page-header-actions.jsx";
import { cardStyles, steelChromeButton } from "@/lib/status-styles.js";
import { cn } from "@/lib/utils.js";

/**
 * Asset layout component that provides the shared header and navigation for asset pages
 * @param {{ children: React.ReactNode }} props - Component props
 * @returns {JSX.Element} Asset layout component
 */
export default function AssetLayout({ children }) {
  const { id } = useParams();
  const { assets: compressors, isLoading: compressorLoading } = useAssets();
  const compressorId = decodeURIComponent(id);
  const compressor = compressors?.find(
    (c) =>
      c.assetNumber === compressorId
      || c.assetNumber === id
      || c.id === compressorId
      || c.id === id
  );

  const [, setLocation] = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Get source parameter from URL to determine where user came from
  const urlParams = new URLSearchParams(window.location.search);
  const sourceParam = urlParams.get("from");

  // Determine back button text and navigation target
  const getBackButtonConfig = () => {
    if (sourceParam === "map") {
      return {
        text: "Back to Fleet Map",
        path: "/map",
      };
    }
    if (sourceParam === "downtime") {
      // The downtime URL needs to include all the parameters that preserves the applied filters in downtime overview
      // (plus and asset to open the details popup for)
      const downtimeParams = new URLSearchParams();
      downtimeParams.set("openAsset", encodeURIComponent(compressor?.id || id));

      const search = urlParams.get("search");
      const dateRange = urlParams.get("dateRange");
      const startDate = urlParams.get("startDate");
      const endDate = urlParams.get("endDate");
      const locations = urlParams.get("locations");
      const vendors = urlParams.get("vendors");

      if (search) downtimeParams.set("search", search);
      if (dateRange) downtimeParams.set("dateRange", dateRange);
      if (startDate) downtimeParams.set("startDate", startDate);
      if (endDate) downtimeParams.set("endDate", endDate);
      if (locations) downtimeParams.set("locations", locations);
      if (vendors) downtimeParams.set("vendors", vendors);

      return {
        text: "Back to Downtime",
        path: `/downtime?${downtimeParams.toString()}`,
      };
    }
    if (sourceParam === "no-data-report") {
      return {
        text: "Back to No-Data Report",
        path: buildBackToSourcePath({
          basePath: "/no-data-report",
          urlParams,
        }),
      };
    }
    if (sourceParam === "operating-points") {
      const page = urlParams.get("page") || "1";
      const perPage = urlParams.get("perPage") || "10";
      const sort = urlParams.get("sort");
      const dir = urlParams.get("dir");
      const tab = urlParams.get("tab");
      const params = new URLSearchParams();
      params.set("page", page);
      params.set("perPage", perPage);
      if (sort) params.set("sort", sort);
      if (dir) params.set("dir", dir);
      if (tab) params.set("tab", tab);
      const query = params.toString();
      return {
        text: "Back to Operating Points",
        path: query ? `/operating-points?${query}` : "/operating-points",
      };
    }
    // Preserve Fleet Overview pagination, sort, and optimization filter when coming from overview
    if (sourceParam === "overview") {
      const page = urlParams.get("page") || "1";
      const perPage = urlParams.get("perPage") || "10";
      const sort = urlParams.get("sort");
      const dir = urlParams.get("dir");
      const optimizationEnabled = urlParams.get("optimizationEnabled");
      const params = new URLSearchParams();
      params.set("page", page);
      params.set("perPage", perPage);
      if (sort) params.set("sort", sort);
      if (dir) params.set("dir", dir);
      if (optimizationEnabled === "Yes" || optimizationEnabled === "No") {
        params.set("optimizationEnabled", optimizationEnabled);
      }
      const query = params.toString();
      return {
        text: "Back to Fleet Overview",
        path: `/?${query}`,
      };
    }
    return {
      text: "Back to Fleet Overview",
      path: "/",
    };
  };

  const backButtonConfig = getBackButtonConfig();

  if (compressorLoading) {
    return (
      <div className='flex h-screen bg-background relative'>
        <MobileMenuButton
          isOpen={isMobileMenuOpen}
          onToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        />
        <Sidebar
          key='persistent-sidebar'
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
        />
        <div className='flex-1 flex flex-col overflow-hidden transition-all duration-200 ease-in-out'>
          <PageHeader
            icon={Cpu}
            title='Loading Asset Details...'
            subtitle='Please wait while we load the asset information'>
            <PageHeaderActions />
          </PageHeader>
          <div className={cn(cardStyles.pageContent)}>
            <LoadingSkeleton data-testid='card-downtime-loading'>
              <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-card px-6 py-4 rounded-t-lg border-b border-border'>
                <div>
                  <LoadingSkeleton />
                </div>
              </div>
              <div className='p-6'>
                <LoadingSkeleton />
              </div>
            </LoadingSkeleton>
          </div>
        </div>
      </div>
    );
  }

  if (!compressor && !compressorLoading) {
    return (
      <div className='flex h-screen bg-background relative'>
        <MobileMenuButton
          isOpen={isMobileMenuOpen}
          onToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        />
        <Sidebar
          key='persistent-sidebar'
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
        />
        <div className='flex-1 flex flex-col overflow-hidden transition-all duration-200 ease-in-out'>
          <PageHeader
            icon={Cpu}
            title='Asset Not Found'
            subtitle='The requested compressor could not be found'>
            <PageHeaderActions />
          </PageHeader>
          <div className={cn(cardStyles.pageContent)}>
            <div className='text-center'>
              <p className='text-muted-foreground mb-4'>
                The requested compressor could not be found.
              </p>
              <Button onClick={() => setLocation("/")}>
                <ArrowLeft className='h-4 w-4 mr-2' />
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='flex h-screen bg-background relative'>
      <MobileMenuButton
        isOpen={isMobileMenuOpen}
        onToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />
      <Sidebar
        key='persistent-sidebar'
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
      />
      <div className='flex-1 flex flex-col overflow-hidden transition-all duration-200 ease-in-out'>
        <PageHeader
          icon={Cpu}
          title={`${compressor.assetNumber}${compressor.lease ? ` - ${compressor.lease}` : ""}`}
          subtitle={compressor.location}>
          <PageHeaderActions />
          <Badge
            variant='status'
            className={getStatusBadgeClass(
              compressor.status,
              "px-3 py-1.5 rounded-full font-medium"
            )}>
            <div
              className={getStatusDotClass(
                compressor.status,
                "w-1.5 h-1.5 rounded-full"
              )}
            />
            {getStatusText(compressor.status)}
          </Badge>
          <CopyCoordinatesButton
            latitude={compressor.latitude}
            longitude={compressor.longitude}
          />
          <Button
            variant='outline'
            size='sm'
            className={steelChromeButton}
            onClick={() => setLocation(backButtonConfig.path)}>
            <ArrowLeft className='h-4 w-4 mr-2' />
            {backButtonConfig.text}
          </Button>
        </PageHeader>

        {/* Navigation Tabs */}
        <div className='bg-card border-b border-border px-4 sm:px-6 py-3 overflow-x-auto'>
          <div className='flex justify-center min-w-max'>
            <AssetNavigationTabs assetId={id} />
          </div>
        </div>

        {/* Main Content — min-h-0 so flex children can scroll (e.g. Root Cause Analysis). */}
        <div className={cn(cardStyles.pageContent, "min-h-0")}>{children}</div>
      </div>
    </div>
  );
}
